import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import { Button, DialogActions, DialogContent, InputAdornment } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TbLock, TbLockPin } from "react-icons/tb";

import { CommonHelperText, CommonInput, CommonLabel } from "@/components/CommonInput";
import { useUpdateUserPasswordMutation } from "@/requests/user.mutations";
import { PasswordSchema, passwordSchema } from "@/schemas/password";
import { useUsersShowPasswordUpdateDialog, useUsersTableActions } from "@/stores/use-users-table";
import { User } from "@/types/user.types";

function UserResetPasswordDialog() {
    const { t } = useTranslation();
    const { updateShowPasswordUpdateDialog } = useUsersTableActions();
    const user = useUsersShowPasswordUpdateDialog() as User;
    const { isPending: loading, mutateAsync } = useUpdateUserPasswordMutation();

    const {
        handleSubmit,
        formState: { errors, isValid },
        register,
    } = useForm({
        defaultValues: {
            password: "",
            passwordConfirm: "",
        },
        disabled: loading,
        resolver: zodResolver(passwordSchema),
    });

    const updatePassword = async (data: PasswordSchema) => {
        if (loading || !user?.id) return;
        try {
            const res = await mutateAsync({ password: data.password, id: user.id });
            const message = res.message || "";
            if (res.ok) {
                handleClose();
                enqueueSnackbar(message, {
                    variant: "success",
                });
            } else {
                enqueueSnackbar(message, {
                    variant: "error",
                });
            }
        } catch (error) {
            console.error("User error:", error);
        }
    };
    const handleClose = () => {
        updateShowPasswordUpdateDialog();
    };

    return (
        <>
            <DialogContent dividers>
                <form autoComplete="off" onSubmit={handleSubmit(updatePassword)} className="grid grid-cols-8 gap-7">
                    <CommonLabel required label={t("password")} className=" col-span-4">
                        <CommonInput
                            error={!!errors?.password}
                            {...register("password")}
                            type="password"
                            placeholder={t("placeholders.enter", { field: t("password") })}
                            startAdornment={
                                <InputAdornment position="start">
                                    <TbLock />
                                </InputAdornment>
                            }
                        />
                        <CommonHelperText text={errors?.password?.message || ""} />
                    </CommonLabel>
                    <CommonLabel required label={t("passwordConfirm")} className=" col-span-4">
                        <CommonInput
                            error={!!errors?.passwordConfirm}
                            type="password"
                            {...register("passwordConfirm")}
                            placeholder={t("placeholders.enter", { field: t("passwordConfirm") })}
                            startAdornment={
                                <InputAdornment position="start">
                                    <TbLockPin />
                                </InputAdornment>
                            }
                        />
                        <CommonHelperText text={errors?.passwordConfirm?.message || ""} />
                    </CommonLabel>
                </form>
            </DialogContent>
            <DialogActions>
                <Button disabled={loading} autoFocus color="secondary" variant="outlined" onClick={handleClose}>
                    {t("buttons.cancel")}
                </Button>

                <LoadingButton
                    disabled={!isValid}
                    loading={loading}
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit(updatePassword)}
                >
                    {t("buttons.update")}
                </LoadingButton>
            </DialogActions>
        </>
    );
}

export default UserResetPasswordDialog;
